import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BrainCircuit, CheckCircle2, Database, FileJson, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import type { Route, User } from '@/types/app';

const PLATFORMS = ['Stripe', 'Shopify', 'Gumroad', 'LemonSqueezy'] as const;

const PROVISIONING_STEPS = [
  { id: 'sync', label: 'Syncing 42 products from catalog',                icon: Database     },
  { id: 'a2a',  label: 'Provisioning A2A endpoint',                       icon: Zap          },
  { id: 'acp',  label: 'Generating Agentic Commerce Protocol (ACP) schemas', icon: FileJson  },
  { id: 'nlip', label: 'Structuring Natural Language Intent vectors',      icon: BrainCircuit },
] as const;

export const OnboardingFlow = ({
  onNavigate, onComplete,
}: { onNavigate: (r: Route) => void; onComplete: (u: User) => void }) => {
  const [step,     setStep]     = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [simState, setSimState] = useState(0);

  useEffect(() => {
    if (step !== 4) return;
    const timers = [
      setTimeout(() => setSimState(1), 300),
      setTimeout(() => setSimState(2), 1800),
      setTimeout(() => setSimState(3), 3800),
      setTimeout(() => setSimState(4), 5300),
    ];
    return () => timers.forEach(clearTimeout);
  }, [step]);

  const handleNext = () => {
    setLoading(true);
    setTimeout(() => { setLoading(false); setStep((s) => s + 1); }, 800);
  };

  const handleComplete = () => {
    const sessionToken = crypto.randomUUID();
    onComplete({
      id:           `u-${sessionToken.slice(0, 8)}`,
      email:        'creator@example.com',
      planTier:     'explorer',
      sessionToken,
    });
    onNavigate('app');
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6 text-zinc-50">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-2 font-bold text-lg">
            <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center
                            shadow-[0_0_15px_rgba(139,92,246,0.5)]">
              <Zap className="w-4 h-4 text-white" />
            </div>
            Elintra
          </div>
          <div className="flex items-center gap-3">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`rounded-full transition-all duration-300 ${
                s < step  ? 'w-2 h-2 bg-violet-500' :
                s === step? 'w-6 h-2 bg-violet-500' : 'w-2 h-2 bg-zinc-700'
              }`} />
            ))}
            <span className="text-sm text-zinc-500 font-mono ml-1">{step} / 4</span>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1 — Account */}
          {step === 1 && (
            <motion.div key="step-1"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              <h2 className="text-3xl font-bold mb-2">Create your account</h2>
              <p className="text-zinc-400 mb-8">Join thousands of creators making their products agent-readable.</p>
              <Card className="p-6 flex flex-col gap-4">
                <Input placeholder="Email address" type="email" defaultValue="creator@example.com" />
                <Input placeholder="Password" type="password" defaultValue="••••••••" />
                <Button className="w-full mt-2" onClick={handleNext} disabled={loading}>
                  {loading ? 'Creating account…' : 'Continue'}
                </Button>
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-800" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-zinc-900 px-3 text-zinc-500">Or continue with</span>
                  </div>
                </div>
                <Button variant="secondary" className="w-full" onClick={handleNext}>
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 2 — Connect Store */}
          {step === 2 && (
            <motion.div key="step-2"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              <h2 className="text-3xl font-bold mb-2">Connect your store</h2>
              <p className="text-zinc-400 mb-8">Where are your products currently hosted?</p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {PLATFORMS.map((platform) => (
                  <button key={platform} type="button" onClick={handleNext}
                    className="p-6 rounded-xl border border-zinc-800 bg-zinc-900/50
                               hover:bg-zinc-800 hover:border-zinc-700 transition-all
                               flex flex-col items-center justify-center gap-3 group cursor-pointer">
                    <Database className="w-8 h-8 text-zinc-500 group-hover:text-violet-400 transition-colors" />
                    <span className="font-medium text-zinc-300 group-hover:text-white transition-colors">
                      {platform}
                    </span>
                  </button>
                ))}
              </div>
              <Button variant="ghost"
                className="w-full text-zinc-500 border border-transparent"
                onClick={handleNext}>
                Skip for now
              </Button>
            </motion.div>
          )}

          {/* Step 3 — Provisioning */}
          {step === 3 && (
            <motion.div key="step-3"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              <h2 className="text-3xl font-bold mb-2">Translating catalog</h2>
              <p className="text-zinc-400 mb-8">Provisioning your protocol endpoints.</p>
              <Card className="p-8">
                <div className="space-y-6">
                  {PROVISIONING_STEPS.map((item, i) => (
                    <motion.div key={item.id}
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.4 + 0.1 }}
                      className="flex items-center gap-4"
                      onAnimationComplete={() => {
                        if (i === PROVISIONING_STEPS.length - 1) setTimeout(handleNext, 600);
                      }}>
                      <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <item.icon className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className="font-medium text-zinc-300 flex-1">{item.label}</span>
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                        transition={{ delay: i * 0.4 + 0.3 }}>
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 4 — Simulation */}
          {step === 4 && (
            <motion.div key="step-4"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              <div className="text-center mb-8">
                <Badge variant="success" className="mb-4">Live Sandbox</Badge>
                <h2 className="text-3xl font-bold mb-2">Your first agent simulation</h2>
                <p className="text-zinc-400">Watch how an AI agent discovers and buys your product.</p>
              </div>
              <Card className="p-8 mb-8 relative overflow-hidden border-violet-500/30">
                <div className="absolute inset-0 bg-gradient-to-b from-violet-500/5 to-transparent pointer-events-none" />
                <div className="relative z-10 space-y-6 min-h-[200px] flex flex-col justify-center">
                  {[
                    { state: 1, label: 'Agent querying: "notion content planner template"', color: 'text-blue-400'   },
                    { state: 2, label: 'Elintra matched: Creator OS Notion System ($39)',    color: 'text-violet-400' },
                    { state: 3, label: 'ACP purchase initiated via Stripe rail',             color: 'text-amber-400'  },
                    { state: 4, label: '✓ Transaction complete — $39.00 settled',            color: 'text-emerald-400'},
                  ].map(({ state, label, color }) =>
                    simState >= state ? (
                      <motion.div key={state}
                        initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                        className={`flex items-center gap-3 font-mono text-sm ${color}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                        {label}
                      </motion.div>
                    ) : null
                  )}
                </div>
              </Card>
              {simState >= 4 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Button className="w-full" size="lg" onClick={handleComplete}>
                    Enter your dashboard →
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
